import React, { useState } from 'react';
import { varAlpha } from 'minimal-shared/utils';

import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import TextsmsIcon from '@mui/icons-material/Textsms';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Box, Modal, Button, TextField, Typography, IconButton } from '@mui/material';

import { useParams } from 'src/routes/hooks';

import { getConfig } from 'src/global-config';
import { useOrganisation } from 'src/organisation';

import type { ModalProps } from '../../../store/modals/types';
//TODO: How will the modal scale to the size of the screen?

const style = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: {
    xs: '90%',
    sm: '450px',
    md: '550px'
  },
  maxWidth: '90vw',
  maxHeight: '90vh',
  overflow: 'auto',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: { xs: 2, sm: 3, md: 4 },
  borderRadius: 2,
};

const shareOptionStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  cursor: 'pointer',
  textAlign: 'center',
  gap: 1,
  width: { xs: '70px', sm: '80px' },
  mb: { xs: 2, sm: 0 },
};

const shareOptionsContainerStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: { xs: 'center', sm: 'space-between' },
  alignItems: 'flex-start',
  width: '100%',
  mt: 2,
  mb: 2,
  gap: { xs: 2, sm: 1 },
};

const JobShareModal: React.FC<ModalProps> = ({ visible, onClose, params }) => {
  const { orgSlug } = useParams();
  const [copied, setCopied] = useState(false);
  const theme = useTheme();
  const { jobCardProps } = params;
  const { organizationSlug: OrganizationSlug } = useOrganisation();
  const { jobSlug, isPublic = false } = jobCardProps;
  const CONFIG = getConfig();
 
  const jobUrl = `${CONFIG.develop.candidateJobPortalUrl}/${isPublic ? orgSlug : OrganizationSlug}/job/${jobSlug}`;

  const iconStyle = {
    width: { xs: 48, sm: 56 },
    height: { xs: 48, sm: 56 },
    padding: 1.5,
    borderRadius: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.08),
    color: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.12),
    },
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(jobUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleShare = (platform: string) => {
    switch (platform) {
      case 'linkedin':
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(jobUrl)}`);
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(jobUrl)}`);
        break;
      case 'email':
        window.open(`mailto:?subject=Check out this job&body=${encodeURIComponent(jobUrl)}`);
        break;
      case 'text':
        // Handle text sharing (this might need a different approach depending on your requirements)
        if (navigator.share) {
          navigator.share({
            title: 'Job Opportunity',
            text: 'Check out this job',
            url: jobUrl,
          });
        }
        break;
      default:
        break;
    }
  };

  return (
    <Modal
      open={visible}
      onClose={onClose}
      aria-labelledby="job-share-modal"
      sx={{
        '& .MuiModal-paper': {
          maxWidth: '90vw',
          maxHeight: '90vh',
        },
      }}
    >
      <Box sx={style}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Share Job
          </Typography>
          {/* TODO: Add a close button with styling*/}
          {/* <Button variant="text" startIcon={<CloseIcon sx={{ width: 18, height: 18 }} />} onClick={onClose} color='inherit' size='small'>
</Button> */}
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box component="div" sx={shareOptionsContainerStyle}>
          <Box component="div" sx={shareOptionStyle} onClick={() => handleShare('text')}>
            <Box sx={iconStyle}>
              <TextsmsIcon sx={{ color: isPublic ? '#1C252E' : '#673ab7' }} />
            </Box>
            <Typography variant="subtitle2">Text</Typography>
          </Box>

          <Box component="div" sx={shareOptionStyle} onClick={() => handleShare('email')}>
            <Box sx={iconStyle}>
              <EmailIcon sx={{ color: isPublic ? '#1C252E' : '#673ab7' }} />
            </Box>
            <Typography variant="subtitle2">Email</Typography>
          </Box>

          <Box component="div" sx={shareOptionStyle} onClick={() => handleShare('linkedin')}>
            <Box sx={iconStyle}>
              <LinkedInIcon sx={{ color: isPublic ? '#1C252E' : '#0077b5' }} />
            </Box>
            <Typography variant="subtitle2">LinkedIn</Typography>
          </Box>

          <Box component="div" sx={shareOptionStyle} onClick={() => handleShare('twitter')}>
            <Box sx={iconStyle}>
              <TwitterIcon sx={{ color: isPublic ? '#1C252E' : '#1da1f2' }} />
            </Box>
            <Typography variant="subtitle2">X(Twitter)</Typography>
          </Box>
        </Box>

        <Box sx={{ 
          mt: 3, 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          flexDirection: { xs: 'column', sm: 'row' }
        }}>
          <TextField
            fullWidth
            value={jobUrl}
            variant="outlined"
            InputProps={{
              readOnly: true,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
              },
              flex: 3,
              width: '100%',
              '& .MuiInputBase-input': {
                py: '12.5px'
              }
            }}
          />
          <Button
            variant="contained"
            startIcon={<ContentCopyIcon />}
            onClick={handleCopyLink}
            size='large'
            sx={{
              flex: { sm: 1 },
              width: { xs: '100%', sm: 'auto' },
            }}
            color={isPublic ? 'inherit' : 'primary'}
          >
            {copied ? 'Copied!' : 'Copy Link'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default JobShareModal;
